import cphereBrainIcon from "../assets/cphere.png";

const STATUS_LABELS = {
  compatible: "Compatible",
  // A shape match whose SUPPLIED rendition is a source master (mastering
  // container and/or above the served resolution). Compatible; conform first.
  compatible_optimization_advised: "Compatible \u00b7 optimization advised",
  // A valid creative format the selected surface does not traffic. The refusal
  // belongs to the placement, never to the creative.
  specialized_inventory: "Specialized inventory",
  not_compatible: "Not compatible",
  optimization_required: "Optimization required",
  review_required: "Review required",
  needs_metadata: "Needs metadata",
  unknown: "Unknown",
  eligible: "Eligible",
  // Compatible, and blocked on a step the uploader can actually take. These
  // exist because `not_eligible` used to absorb them, which put "Not eligible"
  // on the same card as "Compatibility: optimization required".
  eligible_after_optimization: "Eligible after optimization",
  eligible_after_metadata: "Eligible once metadata is saved",
  not_eligible: "Not eligible",
  not_evaluated: "Not evaluated",
  approved: "Approved",
  pending: "Pending",
  rejected: "Rejected",
  not_required: "Not required",
};

const normalizeStatus = (value, fallback = "unknown") =>
  String(value || fallback).trim().toLowerCase();

const labelForStatus = (status) => STATUS_LABELS[normalizeStatus(status)] || String(status || "Unknown");

const normalizePlatformKeyForDisplay = (value) =>
  String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");

const normalizePlatformKeyForFiltering = (value) => {
  const key = normalizePlatformKeyForDisplay(value);
  const compactKey = key.replace(/[^a-z0-9]/g, "");
  if (!compactKey) return "";
  if (compactKey === "linkedin" || compactKey === "linkedinads") return "linkedin";
  if (["meta", "facebook", "fb", "instagram", "ig", "facebookinstagram", "metafacebookinstagram"].includes(compactKey)) return "meta";
  if (["youtube", "youtubeads", "yt"].includes(compactKey)) return "youtube";
  if (["yahoo", "yahoodsp", "yahooads"].includes(compactKey)) return "yahoo";
  if (["viant", "viantdsp", "adelphic"].includes(compactKey)) return "viant";
  if (compactKey === "tiktok") return "tiktok";
  if (["stackadapt", "omni", "cphere"].includes(compactKey)) return "stackadapt";
  if (
    compactKey === "display" ||
    compactKey === "displayprogrammatic" ||
    compactKey === "programmaticdisplay" ||
    compactKey === "programmatic"
  ) {
    return "display";
  }
  return key;
};

const normalizeApprovedPlatformFilterKeys = (value) => {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  return Array.from(new Set(values.map(normalizePlatformKeyForFiltering).filter(Boolean)));
};

const CLIENT_PLATFORM_DISPLAY_OVERRIDES = {
  stackadapt: { label: "Omni", logo: cphereBrainIcon, logoKey: "cphere_brain", vendorVisible: false },
  stack_adapt: { label: "Omni", logo: cphereBrainIcon, logoKey: "cphere_brain", vendorVisible: false },
};

function fallbackInitialsForLabel(label) {
  return String(label || "Platform").trim().slice(0, 2).toUpperCase();
}

const VIANT_PLATFORM_NAME_PATTERN = /^viant(?:[\s_]*dsp)?(?:\s*\(\s*display\s*\))?$/i;

function normalizePlatformDisplayLabel(value, platformKey) {
  const raw = String(value || "").trim();
  const key = normalizePlatformKeyForDisplay(platformKey);
  if (
    key === "display_programmatic" ||
    key === "programmatic_display" ||
    /^display\s*\/\s*programmatic$/i.test(raw) ||
    /^programmatic\s*\/\s*display$/i.test(raw) ||
    /^programmatic\s+display$/i.test(raw)
  ) {
    return "Display";
  }
  if (key === "linkedin" || key === "linkedin_ads" || /^linkedin\s+ads$/i.test(raw)) {
    return "LinkedIn";
  }
  // The governed creative spec (and the eligibility API built on it) still
  // names the platform "Viant DSP". Viant is the platform name, so present it
  // as exactly "Viant" without changing the governed spec identity.
  if (["viant", "viant_dsp", "viantdsp", "adelphic"].includes(key) || VIANT_PLATFORM_NAME_PATTERN.test(raw)) {
    return "Viant";
  }
  // Combined Meta parent chip: show just "Meta" (the chip is the indicator) —
  // never the long "Meta (Facebook / Instagram)". This only collapses the
  // COMBINED key/label; explicit separate `facebook`/`instagram` keys keep their
  // own labels (so downstream FB/IG granularity is preserved).
  if (key === "meta" || /\(\s*facebook\s*\/\s*instagram\s*\)/i.test(raw)) {
    return "Meta";
  }
  return raw;
}

export function getCreativePlatformDisplayConfig(platform = {}) {
  const key = normalizePlatformKeyForDisplay(platform.platformKey);
  const override = CLIENT_PLATFORM_DISPLAY_OVERRIDES[key] || null;
  const label = override?.label ||
    normalizePlatformDisplayLabel(platform.platformLabel, platform.platformKey) ||
    normalizePlatformDisplayLabel(platform.platformKey, platform.platformKey) ||
    "Platform";
  return {
    platformKey: platform.platformKey,
    label,
    logo: override?.logo || "",
    logoKey: override?.logoKey || "",
    hasLogo: Boolean(override?.logoKey),
    fallbackInitials: fallbackInitialsForLabel(label),
    vendorVisible: override?.vendorVisible ?? true,
  };
}

export const resolveEligibilityPlatformDisplay = getCreativePlatformDisplayConfig;

function displayLabelForPlatform(platform = {}) {
  return getCreativePlatformDisplayConfig(platform).label;
}

function sanitizeDisplayText(value) {
  return String(value || "")
    .replace(/Display\s*\/\s*Programmatic/gi, "Display")
    .replace(/Programmatic\s*\/\s*Display/gi, "Display")
    .replace(/Programmatic\s+Display/gi, "Display")
    .replace(/\bViant\s+DSP(?:\s*\(\s*Display\s*\))?/gi, "Viant")
    .replace(/StackAdapt/gi, "Omni")
    .replace(/stackadapt/gi, "Omni");
}

function sanitizeDisplayEntry(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return sanitizeDisplayText(value.message || value.detail || value.reason || "");
  }
  return sanitizeDisplayText(value);
}

function sanitizeDisplayList(values = []) {
  return (Array.isArray(values) ? values : []).map(sanitizeDisplayEntry).filter(Boolean);
}

const uniqueBy = (items, keyFn) => {
  const seen = new Set();
  return (Array.isArray(items) ? items : []).filter((item) => {
    const key = keyFn(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Readiness states that describe a creative which can REACH activation on this
 * platform — now, or after a step the uploader can genuinely take. This is the
 * single definition of "positive path"; the review sheet's default list and the
 * hidden-platform count both derive from it.
 */
const POSITIVE_PATH_READINESS = new Set([
  "ready",
  "blocked_until_optimized",
  "blocked_until_metadata",
]);

const ACTIVATION_READINESS_BY_ELIGIBILITY = {
  eligible: "ready",
  eligible_after_optimization: "blocked_until_optimized",
  eligible_after_metadata: "blocked_until_metadata",
  not_eligible: "blocked_not_compatible",
  not_evaluated: "not_evaluated",
};

/**
 * The normalized per-platform verdict.
 *
 * The backend sends this. It is recomputed here only when the field is absent,
 * which happens while the portals are deployed ahead of the API — the two ship
 * independently, and a review sheet that silently lost its filtering during
 * that window would put the original bug straight back on screen.
 *
 * The reconstruction deliberately mirrors `buildPlatformResolution` in
 * `services/creativePlatformEligibilityService.js`; the two are pinned together
 * by the cross-repository parity suite.
 */
function normalizePlatformResolution(platform) {
  const supplied = platform?.resolution;
  const compatibilityStatus = normalizeStatus(platform?.compatibility?.status);
  const eligibilityStatus = normalizeStatus(platform?.eligibility?.status, "not_evaluated");
  const approvalStatus = normalizeStatus(platform?.approval?.status);
  const missingFields = Array.isArray(platform?.compatibility?.missingFields)
    ? platform.compatibility.missingFields
    : [];

  if (supplied && typeof supplied === "object" && !Array.isArray(supplied)) {
    return {
      fundamentalCompatibility: String(supplied.fundamentalCompatibility || "compatible"),
      metadataCompleteness: String(supplied.metadataCompleteness || "complete"),
      missingFields: Array.isArray(supplied.missingFields) ? supplied.missingFields : missingFields,
      optimizationRequirement: supplied.optimizationRequirement || null,
      approvalStatus: normalizeStatus(supplied.approvalStatus || approvalStatus),
      activationReadiness: String(supplied.activationReadiness || "not_evaluated"),
      reason: String(supplied.reason || ""),
      positivePath: supplied.positivePath === true,
    };
  }

  const optimizationRequirement =
    compatibilityStatus === "optimization_required" ||
    compatibilityStatus === "compatible_optimization_advised"
      ? {
        code: String(platform?.compatibility?.code || "optimization_required"),
        // Cphere performs no compression or transcode; the uploader supplies
        // the conformed file.
        performedByCphere: false,
        actor: "uploader",
      }
      : null;

  // An older payload reports every blocked-but-valid creative as `not_eligible`,
  // so the eligibility status alone cannot say WHY. Recover the real blocker
  // from the compatibility status, otherwise a creative that merely needs
  // metadata or compression would be read as incompatible and filtered out of
  // the sheet during the window where the portal is ahead of the API.
  let activationReadiness;
  if (approvalStatus === "rejected") {
    activationReadiness = "blocked_not_approved";
  } else if (platform?.compatibility?.specReviewRequired) {
    activationReadiness = "blocked_specification_review";
  } else if (ACTIVATION_READINESS_BY_ELIGIBILITY[eligibilityStatus] &&
    eligibilityStatus !== "not_eligible") {
    activationReadiness = ACTIVATION_READINESS_BY_ELIGIBILITY[eligibilityStatus];
  } else if (compatibilityStatus === "needs_metadata") {
    activationReadiness = "blocked_until_metadata";
  } else if (
    compatibilityStatus === "optimization_required" ||
    compatibilityStatus === "compatible_optimization_advised"
  ) {
    activationReadiness = "blocked_until_optimized";
  } else {
    // Includes a compatible creative the approved campaign did not select:
    // technically fine, still not a platform this asset can activate on.
    activationReadiness = "blocked_not_compatible";
  }

  return {
    fundamentalCompatibility:
      platform?.compatibility?.specReviewRequired ? "unverified" : compatibilityStatus === "not_compatible" ? "incompatible" : "compatible",
    metadataCompleteness: compatibilityStatus === "needs_metadata" ? "incomplete" : "complete",
    missingFields,
    optimizationRequirement,
    approvalStatus,
    activationReadiness,
    reason:
      (Array.isArray(platform?.eligibility?.reasons) ? platform.eligibility.reasons[0] : "") ||
      (Array.isArray(platform?.compatibility?.reasons) ? platform.compatibility.reasons[0] : "") ||
      "",
    positivePath: POSITIVE_PATH_READINESS.has(activationReadiness),
  };
}

export function normalizeCreativePlatformEligibilityResponse(payload) {
  const data = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  if (!Array.isArray(data.platforms)) return null;
  return {
    assetId: data.assetId ?? null,
    advertiserId: data.advertiserId ?? null,
    metadataStatus: normalizeStatus(data.metadataStatus, "unavailable"),
    metadata: data.metadata && typeof data.metadata === "object" ? data.metadata : {},
    platforms: data.platforms.map((platform) => ({
      platformKey: String(platform?.platformKey || "").trim(),
      platformLabel: String(platform?.platformLabel || platform?.platformKey || "").trim(),
      campaignApproved: platform?.campaignApproved === true,
      compatibility: {
        status: normalizeStatus(platform?.compatibility?.status),
        code: String(platform?.compatibility?.code || "").trim(),
        recognized: platform?.compatibility?.recognized === true,
        specReviewRequired: platform?.compatibility?.specReviewRequired === true,
        specifications: Array.isArray(platform?.compatibility?.specifications) ? platform.compatibility.specifications : [],
        formatClass: String(platform?.compatibility?.formatClass || "").trim(),
        confidence: normalizeStatus(platform?.compatibility?.confidence, "unconfirmed"),
        reasons: Array.isArray(platform?.compatibility?.reasons) ? platform.compatibility.reasons : [],
        missingFields: Array.isArray(platform?.compatibility?.missingFields)
          ? platform.compatibility.missingFields
          : [],
      },
      eligibility: {
        status: normalizeStatus(platform?.eligibility?.status, "not_evaluated"),
        reasons: Array.isArray(platform?.eligibility?.reasons) ? platform.eligibility.reasons : [],
      },
      approval: {
        status: normalizeStatus(platform?.approval?.status),
        source: String(platform?.approval?.source || "none"),
      },
      // THE NORMALIZED VERDICT. Derived once by the backend and read by every
      // surface. `resolveClientSideResolution` reconstructs it when an older
      // payload predates the field, so the UI has exactly one code path.
      resolution: normalizePlatformResolution(platform),
    })),
    approvedCampaignPlatformKeys: Array.isArray(data.approvedCampaignPlatformKeys)
      ? data.approvedCampaignPlatformKeys
      : [],
    warnings: Array.isArray(data.warnings) ? data.warnings : [],
    errors: Array.isArray(data.errors) ? data.errors : [],
  };
}

// "Cinema" is Cphere's distribution / data-ingestion concept, NOT a paid-media
// creative placement. Out-of-home / theater-lobby / cinema-billboard inventory
// is represented by DOOH. So cinema is excluded from creative platform
// eligibility cards and chips. This is a display-layer filter only — backend
// eligibility rules, reporting, and Cinema ingestion/intelligence are unaffected,
// and DOOH is never fabricated from a cinema verdict (it renders only when the
// backend returns a real DOOH platform with DOOH rules).
const CREATIVE_ELIGIBILITY_EXCLUDED_PLATFORM_KEYS = new Set(["cinema"]);

function isExcludedCreativeMediaPlatform(platform) {
  const key = String(platform?.platformKey || platform?.platformLabel || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return CREATIVE_ELIGIBILITY_EXCLUDED_PLATFORM_KEYS.has(key);
}

export function getCreativePlatformEligibilityRows(eligibility) {
  const normalized = normalizeCreativePlatformEligibilityResponse(eligibility);
  if (!normalized) return [];
  return normalized.platforms
    .filter((platform) => !isExcludedCreativeMediaPlatform(platform))
    .map((platform) => {
    const reasons = sanitizeDisplayList([
      ...platform.compatibility.reasons,
      ...platform.eligibility.reasons,
    ].filter(Boolean));
    const isCompatible = platform.compatibility.status === "compatible";
    // Compatible, but not in its delivered form yet. Kept OUT of `isCompatible`
    // so the summary can name the conform work instead of implying the asset is
    // ready to traffic — the compatibility claim itself is unchanged.
    const isCompatibleWithAdvisory =
      platform.compatibility.status === "compatible_optimization_advised";
    const isSpecializedInventory =
      platform.compatibility.status === "specialized_inventory";
    const isEligible = platform.eligibility.status === "eligible";
    const resolution = platform.resolution;
    // The summary badge is derived from the SAME resolution that the status
    // lines and the explanatory copy read.
    //
    // It used to be a chain of `else if`s over compatibility statuses with
    // "Not compatible with this platform" as the default, so any status the
    // chain did not enumerate fell through to that default.
    // `optimization_required` was one of them \u2014 which is how a card came to
    // show "Not compatible with this platform" directly above
    // "Compatibility: Optimization required". Switching on the normalized
    // activation readiness makes an unhandled state impossible.
    let summaryTone = "neutral";
    let summaryLabel = "Not compatible with this platform";
    switch (resolution.activationReadiness) {
      case "ready":
        summaryTone = "eligible";
        summaryLabel = "Eligible for this platform";
        break;
      case "blocked_until_optimized":
        summaryTone = "warn";
        summaryLabel = isCompatibleWithAdvisory
          ? "Compatible \u2014 conform the source master before activation"
          : "Compatible \u2014 compress before activation";
        break;
      case "blocked_until_metadata":
        summaryTone = "pending";
        summaryLabel = "Compatible \u2014 save the missing metadata";
        break;
      case "blocked_specification_review":
        summaryTone = "warn";
        summaryLabel = "Placement specifications need verification";
        break;
      case "blocked_not_approved":
        summaryTone = "warn";
        summaryLabel = "Approval required for this platform";
        break;
      default:
        if (isSpecializedInventory) {
          summaryTone = "warn";
          summaryLabel = "Valid format \u2014 needs an eligible interstitial placement";
        }
        break;
    }
    return {
      platformKey: platform.platformKey,
      platformLabel: displayLabelForPlatform(platform),
      platformDisplayConfig: getCreativePlatformDisplayConfig(platform),
      compatibilityStatus: platform.compatibility.status,
      compatibilityCode: platform.compatibility.code,
      campaignApproved: platform.campaignApproved,
      recognized: platform.compatibility.recognized,
      specReviewRequired: platform.compatibility.specReviewRequired,
      specifications: platform.compatibility.specifications,
      formatClass: platform.compatibility.formatClass,
      compatibilityLabel: labelForStatus(platform.compatibility.status),
      compatibilityConfidence: platform.compatibility.confidence,
      eligibilityStatus: platform.eligibility.status,
      eligibilityLabel: labelForStatus(platform.eligibility.status),
      approvalStatus: platform.approval.status,
      approvalLabel: labelForStatus(platform.approval.status),
      missingFields: platform.compatibility.missingFields,
      reasons,
      isCompatible,
      isCompatibleWithAdvisory,
      isSpecializedInventory,
      isEligible,
      isApproved: platform.approval.status === "approved",
      summaryLabel,
      summaryTone,
      // The normalized verdict, carried through so the card renders exactly
      // what the summary and the badge were derived from. Its reason goes
      // through the same vendor-label sanitizer as `reasons`, which strips
      // "Display / Programmatic" to "Display" and "StackAdapt" to "Omni" \u2014
      // reading it raw would have leaked internal vendor naming into the card.
      resolution: { ...resolution, reason: sanitizeDisplayText(resolution.reason) },
      // ONE sentence, already written for a human by the resolver. The card
      // used to print `reasons.slice(0, 2)`, and those entries carried machine
      // codes composed as `${format}:${code}` \u2014 which is how reviewers were
      // shown "VIDEO:UNSUPPORTED_FILE_TYPE" and
      // "STANDARD_BANNER:SERVED_WEIGHT_EXCEEDS_LIMIT".
      explanation: sanitizeDisplayText(resolution.reason) || reasons[0] || "",
      // Missing fields are only a real next step when nothing fundamental
      // failed; an unsupported asset must never be presented as one save away
      // from working.
      actionableMissingFields:
        resolution.fundamentalCompatibility === "compatible"
          ? resolution.missingFields
          : [],
      positivePath: resolution.positivePath,
      activationReadiness: resolution.activationReadiness,
    };
  });
}

/**
 * The platforms the review sheet shows by default: those where the asset can
 * actually reach activation, now or after a real step.
 *
 * The sheet used to enumerate every platform in the registry, so a static
 * banner was presented alongside CTV and TikTok cards whose only content was a
 * refusal. `includeAllPlatforms` restores the full diagnostic list on request.
 */
/**
 * The sentence above the platform cards, stated from the resolved rows.
 *
 * The sheet previously rendered a fixed paragraph claiming "This asset is
 * eligible for its selected platform" no matter what the cards below it said,
 * which is one half of the contradiction reviewers reported. Saying nothing is
 * better than asserting something the cards disprove, so an empty string is a
 * valid result here.
 */
export function buildPlatformSummarySentence(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return "";
  const ready = list.filter((row) => row.activationReadiness === "ready");
  const afterStep = list.filter(
    (row) =>
      row.activationReadiness === "blocked_until_optimized" ||
      row.activationReadiness === "blocked_until_metadata"
  );
  const names = (entries) => entries.map((row) => row.platformLabel).join(", ");

  if (ready.length && !afterStep.length) {
    return `Ready to activate on ${names(ready)}.`;
  }
  if (ready.length && afterStep.length) {
    return `Ready to activate on ${names(ready)}. ${names(afterStep)} needs one more step before activation.`;
  }
  if (afterStep.length) {
    return `Compatible with ${names(afterStep)} \u2014 one step remains before activation.`;
  }
  return "This asset has no activatable platform in the selected campaign scope.";
}

export function getCreativeReviewPlatformRows(eligibility, { includeAllPlatforms = false } = {}) {
  const rows = getCreativePlatformEligibilityRows(eligibility);
  if (includeAllPlatforms) return rows;
  return rows.filter((row) => row.positivePath);
}

/**
 * How many platforms the default view is hiding, so the sheet can offer the
 * full list honestly rather than silently truncating.
 */
export function countHiddenCreativeReviewPlatforms(eligibility) {
  const rows = getCreativePlatformEligibilityRows(eligibility);
  return rows.filter((row) => !row.positivePath).length;
}

export function formatCreativeEligibilityMetadata(metadata = {}) {
  const width = Number(metadata.width);
  const height = Number(metadata.height);
  const source = normalizeStatus(metadata.source, "unavailable");
  const inferredFields = Array.isArray(metadata.inferredFields) ? metadata.inferredFields : [];
  const hasInferredDimensions = inferredFields.includes("width") || inferredFields.includes("height");
  const hasInferredAspect = inferredFields.includes("aspectRatio") || inferredFields.includes("ratioLabel");
  const provenance =
    hasInferredDimensions
      ? "Dimensions inferred from filename."
      : hasInferredAspect
        ? "Aspect ratio inferred from filename."
      : source === "measured"
        ? "Metadata measured from upload."
        : source === "drive_media_metadata"
          ? "Metadata measured from Drive."
        : source === "legacy"
          ? "Metadata read from legacy saved fields."
          : "Needs metadata";
  return [
    { label: "Kind", value: metadata.assetKind || "Unknown" },
    {
      label: "Dimensions",
      value: Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
        ? `${width} x ${height}`
        : "Needs metadata",
    },
    {
      label: "Duration",
      value: metadata.durationSeconds != null ? `${Number(metadata.durationSeconds).toFixed(1)}s` : "Not required",
    },
    {
      label: "Aspect Ratio",
      value: metadata.ratioLabel || (metadata.aspectRatio != null ? String(metadata.aspectRatio) : "Needs metadata"),
    },
    { label: "MIME Type", value: metadata.mimeType || "Unknown" },
    { label: "Metadata Source", value: provenance },
  ];
}

export function buildCreativePlatformEligibilityDisplay(input, options = {}) {
  const state = input && typeof input === "object" && !Array.isArray(input) ? input : {};
  const eligibility = normalizeCreativePlatformEligibilityResponse(state.data || state);

  if (!eligibility) {
    if (state.error) {
      return {
        source: "unavailable",
        statusLabel: "Eligibility unavailable",
        detailLabel: "Backend eligibility could not be loaded.",
        tableStatusLabel: "Eligibility unavailable",
        tableDetailLabel: "Open details for metadata status.",
        tableApprovalLabel: "",
        statusTitle: "Eligibility unavailable — backend eligibility could not be loaded.",
        globallyRecognizedPlatformKeys: [],
        approvedCampaignPlatformKeys: [],
        campaignRecognizedPlatformKeys: [],
        campaignRecognizedPlatformLabels: [],
        campaignCompatibilityVerdicts: [],
        platformIdentityLabel: "—",
        compatibilityLabel: "Eligibility unavailable",
        compatibilityDetail: "Backend eligibility could not be loaded.",
        compatibilityTone: "unavailable",
        compatibilityTitle: "Eligibility unavailable — backend eligibility could not be loaded.",
        detectedDimensions: "",
        detectedFileType: "",
        compatiblePlatformKeys: [],
        compatiblePlatformLabels: [],
        eligiblePlatformLabels: [],
        platformRows: [],
        metadataRows: [],
        warnings: [],
        errors: [String(state.error)],
        isLoading: false,
        isUnavailable: true,
        needsMetadata: false,
      };
    }
    return {
      source: "loading",
      statusLabel: "Checking eligibility",
      detailLabel: "Loading backend eligibility.",
      tableStatusLabel: "Checking eligibility",
      tableDetailLabel: "Backend eligibility is loading.",
      tableApprovalLabel: "",
      statusTitle: "Checking eligibility — backend eligibility is loading.",
      globallyRecognizedPlatformKeys: [],
      approvedCampaignPlatformKeys: [],
      campaignRecognizedPlatformKeys: [],
      campaignRecognizedPlatformLabels: [],
      campaignCompatibilityVerdicts: [],
      platformIdentityLabel: "—",
      compatibilityLabel: "Checking eligibility",
      compatibilityDetail: "Backend eligibility is loading.",
      compatibilityTone: "pending",
      compatibilityTitle: "Checking eligibility — backend eligibility is loading.",
      detectedDimensions: "",
      detectedFileType: "",
      compatiblePlatformKeys: [],
      compatiblePlatformLabels: [],
      eligiblePlatformLabels: [],
      platformRows: [],
      metadataRows: [],
      warnings: [],
      errors: [],
      isLoading: true,
      isUnavailable: false,
      needsMetadata: false,
    };
  }

  const platformRows = getCreativePlatformEligibilityRows(eligibility);
  const approvedPlatformFilterKeys = normalizeApprovedPlatformFilterKeys(
    options.approvedPlatformKeys ?? state.approvedPlatformKeys ?? eligibility.approvedCampaignPlatformKeys
  );
  const hasApprovedCampaignScope = approvedPlatformFilterKeys.length > 0;
  // FAIL CLOSED. With no approved campaign strategy there is no governed
  // platform scope, so nothing may be presented. Falling back to "all
  // platforms" would surface platforms the campaign never selected — the exact
  // failure this governance exists to prevent.
  const visiblePlatformRows = hasApprovedCampaignScope
    ? platformRows.filter((row) => approvedPlatformFilterKeys.includes(
      normalizePlatformKeyForFiltering(row.platformKey || row.platformLabel)
    ))
    : [];
  // Deterministic ordering: the approved campaign's own platform order.
  const campaignOrderIndex = (row) => {
    const index = approvedPlatformFilterKeys.indexOf(
      normalizePlatformKeyForFiltering(row.platformKey || row.platformLabel)
    );
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };
  const compatibleRows = uniqueBy(
    visiblePlatformRows.filter((row) => row.isCompatible),
    (row) => row.platformKey || row.platformLabel
  );
  const eligibleRows = compatibleRows.filter((row) => row.isEligible);
  const hasApproved = visiblePlatformRows.some((row) => row.approvalStatus === "approved");
  const hasRejected = visiblePlatformRows.some((row) => row.approvalStatus === "rejected");
  const hasPending = visiblePlatformRows.some((row) => row.approvalStatus === "pending");
  const needsMetadata =
    eligibility.metadataStatus === "needs_metadata" ||
    visiblePlatformRows.some((row) => row.compatibilityStatus === "needs_metadata");

  // Rows that reached a DEFINITIVE verdict. These must always win over the
  // pending branch below: a completed evaluation is never "pending".
  //
  // This ordering is the fix for the masking defect. `needsMetadata` is a
  // cross-platform flag — a static banner legitimately trips it because the
  // video platforms (LinkedIn/YouTube/CTV) report a missing `duration` they can
  // never have. Previously that flag was tested FIRST, so a definitive Display
  // verdict ("valid size, but over the served-weight limit") was discarded and
  // the row fell through to the neutral literal "Compatibility pending",
  // leaving the operator with no way to see the real reason.
  const optimizationRows = visiblePlatformRows.filter(
    (row) => row.compatibilityStatus === "optimization_required"
  );
  const reviewRows = visiblePlatformRows.filter(
    (row) => row.compatibilityStatus === "review_required"
  );
  const advisedRows = uniqueBy(
    visiblePlatformRows.filter((row) => row.isCompatibleWithAdvisory),
    (row) => row.platformKey || row.platformLabel
  );
  const specializedRows = visiblePlatformRows.filter(
    (row) => row.isSpecializedInventory
  );
  const incompatibleRows = visiblePlatformRows.filter(
    (row) => row.compatibilityStatus === "not_compatible"
  );
  const firstReason = (rows) => rows.map((row) => (row.reasons || [])[0]).find(Boolean) || "";

  // PLATFORM IDENTITY — which platforms RECOGNIZE this creative's format. This
  // is deliberately independent of whether they can currently traffic it: a
  // creative does not stop being a Display creative because it is too heavy or
  // because the selected activation surface only takes banners.
  // Campaign-visible identity = recognition INTERSECTED with the approved
  // campaign scope, in the campaign's canonical order. Global recognition is
  // kept only as a diagnostic and must never reach the operator.
  const campaignRecognizedRows = uniqueBy(
    visiblePlatformRows.filter((row) => row.recognized),
    (row) => row.platformKey || row.platformLabel
  ).sort((left, right) => campaignOrderIndex(left) - campaignOrderIndex(right));
  const globallyRecognizedRows = uniqueBy(
    platformRows.filter((row) => row.recognized),
    (row) => row.platformKey || row.platformLabel
  );
  // Compatibility state is reported SEPARATELY from platform identity so the
  // two are never rendered interchangeably.

  let compatibilityLabel = "Incompatible";
  let compatibilityDetail = "No confirmed compatible platform specs were found.";
  let compatibilityTone = "blocked";

  let statusLabel = "No compatible platforms";
  let detailLabel = "No confirmed compatible platform specs were found.";
  let tableStatusLabel = statusLabel;
  let tableDetailLabel = detailLabel;
  if (compatibleRows.length) {
    compatibilityLabel = "Compatible";
    compatibilityDetail = eligibleRows.length
      ? `Eligible: ${eligibleRows.map((row) => row.platformLabel).join(" · ")}`
      : "Matches the governed specification for this platform.";
    compatibilityTone = "eligible";
    statusLabel = compatibleRows.map((row) => row.platformLabel).join(" · ");
    detailLabel = eligibleRows.length
      ? `Eligible: ${eligibleRows.map((row) => row.platformLabel).join(" · ")}`
      : "Compatible; not eligible for selected context";
    tableStatusLabel = statusLabel;
    tableDetailLabel = detailLabel;
  } else if (advisedRows.length) {
    // COMPATIBLE. The creative matches the platform specification; only the
    // supplied rendition is a master (mastering container and/or above the
    // served resolution). Reporting this as incompatible was the defect \u2014 a
    // 4K ProRes master is the normal deliverable, not a broken file.
    compatibilityLabel = "Compatible \u00b7 Optimization Advised";
    compatibilityDetail = firstReason(advisedRows) ||
      "Matches the platform specification; conform the source master before activation.";
    compatibilityTone = "warn";
    statusLabel = advisedRows.map((row) => row.platformLabel).join(" \u00b7 ");
    detailLabel = compatibilityDetail;
    tableStatusLabel = statusLabel;
    tableDetailLabel = detailLabel;
  } else if (optimizationRows.length) {
    // Dimensionally valid Display creative that exceeds the governed served
    // weight. Named for the platform so it reads as "Display — Optimization
    // Required" rather than a generic failure.
    const platformName = optimizationRows[0].platformLabel || "Display";
    // The platform name lives in the Platform column; the state stands alone.
    compatibilityLabel = "Optimization Required";
    compatibilityDetail = firstReason(optimizationRows) ||
      "Valid dimensions; compress the source asset before activation.";
    compatibilityTone = "warn";
    statusLabel = `${platformName} — Optimization Required`;
    detailLabel = firstReason(optimizationRows) ||
      "Valid dimensions; compress the source asset before activation.";
    tableStatusLabel = statusLabel;
    tableDetailLabel = detailLabel;
  } else if (reviewRows.length) {
    compatibilityLabel = "Review Required";
    compatibilityDetail = firstReason(reviewRows) ||
      "No governed activation specification exists for this size on this platform.";
    compatibilityTone = "warn";
    statusLabel = "Review Required";
    detailLabel = firstReason(reviewRows) ||
      "No governed activation specification exists for this size on this platform.";
    tableStatusLabel = statusLabel;
    tableDetailLabel = detailLabel;
  } else if (specializedRows.length) {
    // A VALID creative format that this surface does not traffic. The previous
    // "Unsupported for Standard Display Activation" described the surface's
    // limits as though they were the creative's defect.
    compatibilityLabel = "Specialized Inventory";
    compatibilityDetail = firstReason(specializedRows) ||
      "Valid creative format. Requires an eligible interstitial placement.";
    compatibilityTone = "warn";
    statusLabel = compatibilityLabel;
    detailLabel = compatibilityDetail;
    tableStatusLabel = statusLabel;
    tableDetailLabel = detailLabel;
  } else if (incompatibleRows.length) {
    // A recognized Display creative refused by THIS platform's activation
    // surface is scoped to that platform. Only a genuinely unrecognized size
    // gets the bare, universal "Incompatible".
    const crossClass = incompatibleRows.find(
      (row) => row.compatibilityCode === "unsupported_activation_format"
    );
    // "Incompatible for Display" reads as a contradiction against a creative we
    // recognize AS Display. Scope the refusal to the ACTIVATION SURFACE for the
    // generic Display platform, and to the destination when one is selected.
    const crossClassIsGenericDisplay = crossClass
      ? normalizePlatformKeyForFiltering(crossClass.platformKey || crossClass.platformLabel) === "display"
      : false;
    compatibilityLabel = crossClass
      ? (crossClassIsGenericDisplay
        ? "Unsupported for Standard Display Activation"
        : `Incompatible for ${crossClass.platformLabel}`)
      : "Incompatible";
    compatibilityDetail = firstReason(crossClass ? [crossClass] : incompatibleRows) ||
      "No confirmed compatible platform specs were found.";
    compatibilityTone = "blocked";
    statusLabel = compatibilityLabel;
    detailLabel = compatibilityDetail;
    tableStatusLabel = statusLabel;
    tableDetailLabel = detailLabel;
  } else if (needsMetadata) {
    // Name the exact missing spec so the pending state is actionable rather than
    // a vague "Specs pending". Aggregate the missing fields the backend flagged
    // on the needs-metadata platform checks.
    const missing = new Set();
    visiblePlatformRows.forEach((row) => {
      if (row.compatibilityStatus !== "needs_metadata") return;
      (row.missingFields || []).forEach((field) => {
        const normalizedField = String(field || "").trim().toLowerCase();
        if (!normalizedField) return;
        if (["width", "height", "dimensions", "aspectratio", "ratiolabel"].includes(normalizedField)) {
          missing.add("dimensions");
        } else if (["duration", "durationseconds"].includes(normalizedField)) {
          missing.add("duration");
        } else {
          missing.add("metadata");
        }
      });
    });
    // Duration is only relevant to VIDEO assets. A static image/banner can pick
    // up `duration` missingFields from video-only platform checks (YouTube/CTV)
    // in the matrix — those must NOT drive the row label to "Duration required".
    const assetKind = String(eligibility.metadata?.assetKind || "").trim().toLowerCase();
    const isVideoAsset =
      assetKind === "video" ||
      String(eligibility.metadata?.mimeType || "").toLowerCase().startsWith("video/");
    const needsDimensions = missing.has("dimensions");
    const needsDuration = missing.has("duration") && isVideoAsset;
    // Neutral fallback when the only missing fields come from platform checks
    // that aren't relevant to this asset (e.g. duration on an image): point the
    // user to the matrix instead of demanding an irrelevant spec.
    let pendingLabel = "Compatibility pending";
    let pendingDetail = "Open Platform Compatibility Checks for platform-specific requirements.";
    if (needsDimensions && needsDuration) {
      pendingLabel = "Specs pending";
      pendingDetail = "Add dimensions and duration to confirm platform compatibility.";
    } else if (needsDuration) {
      pendingLabel = "Duration required";
      pendingDetail = "Add video duration to confirm platform compatibility.";
    } else if (needsDimensions) {
      pendingLabel = "Dimensions required";
      pendingDetail = "Add width and height to confirm platform compatibility.";
    }
    compatibilityLabel = pendingLabel;
    compatibilityDetail = pendingDetail;
    compatibilityTone = "pending";
    statusLabel = pendingLabel;
    detailLabel = pendingDetail;
    tableStatusLabel = pendingLabel;
    tableDetailLabel = pendingDetail;
  } else if (!visiblePlatformRows.length) {
    // GOVERNED NO-MATCH. The approved campaign scope left NOTHING to evaluate —
    // either no approved strategy defines a scope at all, or none of the
    // platforms the backend returned is in that scope. The explanation must
    // never name a platform the campaign did not select.
    //
    // Gated on `visiblePlatformRows`, NOT on recognition. Governance decides
    // WHICH platforms are visible; it never overrides the verdict an in-scope
    // platform actually reached. An in-scope platform that refuses an
    // unrecognized creative still reports its own "Incompatible" above.
    compatibilityLabel = "No approved campaign platform match";
    compatibilityDetail = hasApprovedCampaignScope
      ? "No platform selected in the approved campaign recognizes this creative's format."
      : "No approved campaign strategy defines a platform scope for this advertiser.";
    compatibilityTone = "blocked";
    statusLabel = "Unassigned";
    detailLabel = compatibilityDetail;
    tableStatusLabel = statusLabel;
    tableDetailLabel = compatibilityDetail;
  }

  const approvalLabel = hasApproved
    ? "Approval: Approved"
    : hasRejected
      ? "Approval: Rejected"
      : hasPending
        ? "Approval: Pending"
        : "";

  // Accessible, self-contained explanation for the compact table cell, which
  // renders a bare status word with no room for detail. Carries the state, the
  // detected dimensions and file type, and the governing reason so a
  // non-compatible row is understandable without opening dev tools — and
  // without relying on colour to convey the state.
  const meta = eligibility.metadata || {};
  const metaWidth = Number(meta.width);
  const metaHeight = Number(meta.height);
  const detectedDimensions =
    Number.isFinite(metaWidth) && Number.isFinite(metaHeight) && metaWidth > 0 && metaHeight > 0
      ? `${metaWidth}x${metaHeight}`
      : "dimensions unknown";
  const detectedFileType = meta.mimeType || meta.assetKind || "file type unknown";
  const statusTitle = [
    tableStatusLabel,
    `Detected: ${detectedDimensions}, ${detectedFileType}`,
    tableDetailLabel,
  ]
    .filter(Boolean)
    .join(" — ");

  return {
    source: "backend",
    statusLabel,
    detailLabel,
    tableStatusLabel,
    tableDetailLabel,
    statusTitle,
    detectedDimensions,
    detectedFileType,
    // --- API separation. Platform identity — never a compatibility verdict.
    // The Platform column consumes ONLY campaignRecognizedPlatformKeys. ---
    // Internal diagnostic: every platform whose registry recognized this
    // creative, ignoring campaign scope. NOT operator-visible identity.
    globallyRecognizedPlatformKeys: globallyRecognizedRows.map((row) => row.platformKey),
    // Campaign governance: the approved campaign's selected platforms.
    approvedCampaignPlatformKeys: approvedPlatformFilterKeys,
    // The intersection — the only identity the UI may render.
    campaignRecognizedPlatformKeys: campaignRecognizedRows.map((row) => row.platformKey),
    campaignRecognizedPlatformLabels: campaignRecognizedRows.map((row) => row.platformLabel),
    // Verdicts for approved platforms only.
    campaignCompatibilityVerdicts: visiblePlatformRows.map((row) => ({
      platformKey: row.platformKey,
      platformLabel: row.platformLabel,
      status: row.compatibilityStatus,
      code: row.compatibilityCode,
      reasons: row.reasons,
    })),
    // NEVER "Unrecognized format": Creative Studio derives and displays a real
    // format for this asset in its own column, so claiming the format is
    // unrecognized contradicts the row beside it. The honest fact is that no
    // platform in scope MATCHED it — which, under campaign governance, means
    // no platform the approved campaign selected recognized this creative.
    platformIdentityLabel: campaignRecognizedRows.length ? "" : "No platform match",
    // Compatibility state — never a platform identity.
    compatibilityLabel,
    compatibilityDetail,
    compatibilityTone,
    compatibilityTitle: [
      compatibilityLabel,
      `Detected: ${detectedDimensions}, ${detectedFileType}`,
      compatibilityDetail,
    ].filter(Boolean).join(" — "),
    tableApprovalLabel: "",
    approvalLabel,
    compatiblePlatformKeys: compatibleRows.map((row) => row.platformKey),
    compatiblePlatformLabels: compatibleRows.map((row) => row.platformLabel),
    eligiblePlatformLabels: eligibleRows.map((row) => row.platformLabel),
    platformRows: visiblePlatformRows,
    // DEFAULT REVIEW LIST. Only platforms where the asset has a real path to
    // activation. The full campaign-scoped list stays on `platformRows` for the
    // diagnostic view, and `hiddenPlatformCount` keeps the omission visible
    // instead of silently truncating.
    positivePathPlatformRows: visiblePlatformRows.filter((row) => row.positivePath),
    hiddenPlatformCount: visiblePlatformRows.filter((row) => !row.positivePath).length,
    // The summary sentence above the cards, derived rather than asserted. The
    // sheet used to hard-code "This asset is eligible for its selected
    // platform." regardless of what the payload actually said.
    platformSummarySentence: buildPlatformSummarySentence(visiblePlatformRows),
    metadataRows: formatCreativeEligibilityMetadata(eligibility.metadata),
    warnings: sanitizeDisplayList(eligibility.warnings),
    errors: sanitizeDisplayList(eligibility.errors),
    isLoading: false,
    isUnavailable: false,
    needsMetadata,
  };
}
